import { ageEnAnnees } from '@colette/shared';

// Mise en forme des dates et heures.
//
// Deux natures de valeurs, qui ne se traitent PAS pareil :
//
//   - un INSTANT (`startsAt`, `dueAt`, `remindAt`) : une date ISO complete. Son heure
//     d'affichage depend du fuseau du foyer.
//   - un JOUR CALENDAIRE (`days[].date`, « 2026-08-26 ») : deja calcule par le serveur
//     dans le fuseau du foyer. Le reconvertir serait une faute — `new Date('2026-08-26')`
//     vaut minuit UTC, et l'afficher dans un fuseau en retard rendrait la veille.
//     On l'ancre donc a midi UTC et on le formate en UTC : le jour ne peut plus glisser.

const MIDI = 'T12:00:00Z';

/** Heure d'un instant, dans le fuseau du foyer. Ex. « 09:20 ». */
export function heureDe(instantIso: string, fuseau: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: fuseau,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(instantIso));
}

/**
 * Jour calendaire d'un INSTANT, dans le fuseau du foyer. Ex. « 2026-08-27 ».
 *
 * Sert a remplir un champ de date depuis un evenement existant. A ne pas confondre avec
 * un jour deja calcule par le serveur, qu'il ne faut surtout pas reconvertir.
 */
export function jourDe(instantIso: string, fuseau: string): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: fuseau }).format(new Date(instantIso));
}

/** Jour calendaire en toutes lettres. Ex. « mercredi 26 août ». */
export function jourEnToutesLettres(jour: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(jour + MIDI));
}

/** Abrege pour la bande de jours du planning. Ex. { jour: 'mer', num: '26' }. */
export function jourAbrege(jour: string): { jour: string; num: string } {
  const date = new Date(jour + MIDI);
  return {
    jour: new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', weekday: 'short' })
      .format(date)
      .replace('.', ''),
    num: new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', day: 'numeric' }).format(date),
  };
}

/** Jour calendaire d'aujourd'hui dans le fuseau du foyer, au format AAAA-MM-JJ. */
export function aujourdhuiDans(fuseau: string): string {
  // `fr-CA` rend justement AAAA-MM-JJ. Meme procede que `todayInZone` cote serveur.
  return new Intl.DateTimeFormat('fr-CA', { timeZone: fuseau }).format(new Date());
}

/** Decale un jour calendaire de N jours, sans jamais passer par un fuseau. */
export function jourDecale(jour: string, jours: number): string {
  const date = new Date(jour + MIDI);
  date.setUTCDate(date.getUTCDate() + jours);
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'UTC' }).format(date);
}

/** Age d'un element de la boite, en langage courant. */
export function ilYA(instantIso: string): string {
  const minutes = Math.floor((Date.now() - new Date(instantIso).getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.floor(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  const jours = Math.floor(heures / 24);
  if (jours === 1) return 'hier';
  if (jours < 7) return `il y a ${jours} jours`;
  return jourEnToutesLettres(new Intl.DateTimeFormat('fr-CA', { timeZone: 'UTC' }).format(new Date(instantIso)));
}

/**
 * Distance d'un jour calendaire futur, en langage courant : « demain », « dans 5
 * jours », « dans 3 semaines », « dans 2 mois », « dans plus d'un an ». Arithmetique de
 * jours purs — aucun fuseau, les deux jours sont deja ceux du foyer.
 */
export function dansCombienDeTemps(jourCible: string, aujourdhui: string): string {
  const jours = Math.round(
    (new Date(jourCible + MIDI).getTime() - new Date(aujourdhui + MIDI).getTime()) / 86_400_000,
  );
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return 'demain';
  if (jours < 7) return `dans ${jours} jours`;
  if (jours < 30) {
    const semaines = Math.round(jours / 7);
    return `dans ${semaines} semaine${semaines > 1 ? 's' : ''}`;
  }
  if (jours < 365) {
    const mois = Math.round(jours / 30.44);
    return `dans ${mois} mois`;
  }
  return "dans plus d'un an";
}

/**
 * Depuis combien de temps une echeance est passee, en langage courant : « hier »,
 * « 3 jours ». Arithmetique de jours purs, les deux jours etant deja ceux du foyer.
 */
export function depuisCombienDeJours(jourEcheance: string, aujourdhui: string): string {
  const jours = Math.round(
    (new Date(aujourdhui + MIDI).getTime() - new Date(jourEcheance + MIDI).getTime()) / 86_400_000,
  );
  if (jours <= 1) return 'hier';
  return `${jours} jours`;
}

/** « 3 ans », « 1 an », « moins d'un an » — pour l'ecran Reglages. */
export function agePourAffichage(naissance: string): string {
  const age = ageEnAnnees(naissance, new Intl.DateTimeFormat('fr-CA').format(new Date()));
  if (age <= 0) return "moins d'un an";
  return `${age} an${age > 1 ? 's' : ''}`;
}
